import { Avatar, Button, Card, Checkbox, Flex, Group, Space, Stack, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { IconChevronDown, IconChevronUp, IconInfoCircle } from "@tabler/icons-react";
import { Timestamp } from "firebase/firestore";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetAuthorizations } from "@/hooks/useGetAuthorizations";
import useGetColaboradorById from "@/modules/colaboradores/hooks/useGetColaboradorById";
import { getTiposDeRespostasItem } from "@/modules/tipos-de-respostas/firestore";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import { ID_EXTRAS_DESCONSIDERAR_AVALIACAO, ID_EXTRAS_VISUALIZAR_NOTAS_DE_AVALIADORES } from "@/utils/constants";
import EditaNotaItem from "./EditaNotaItem";
import NotaItem from "./NotaItem";
import { useAddNota } from "./useGetInscricao";
import useQueryMedias from "./useGetMediaFromUrl";
import useGetProjetoFromUrl from "./useGetProjetoFromUrl";
import useGetQuestionarioFromUrl from "./useGetQuestionarioFromUrl";

const STRING_TRIM_LENGTH = -2;

interface Fase {
  id: string;
  titulo: string;
  cor: string;
  checked: boolean;
}

interface Objeto {
  resposta: RespostaItem | RespostaItem[] | number | string;
}

interface RespostaItem {
  valor: number | string;
}

type CardHeaderProperties = {
  opened: boolean;
  toggle: () => void;
  qtdeAvaliacoes: number | undefined;
};

type Properties = {
  pergunta: TPergunta | null | undefined;
  numero: number;
  statusItem: TStatus | undefined;
  respostas: TResposta[];
  grupo: TGruposDiagnostico;
};
type FirestoreTipoResposta = {
  campos: { campo: string; valor: string }[];
};

interface AvaliacaoItemProperties {
  item: TMedia;
  faseTitulo: string;
  empresaAtivaId: string | undefined;
  refetch: () => void;
}

function AvaliacaoItem({ item, faseTitulo, empresaAtivaId, refetch }: AvaliacaoItemProperties) {
  const { mutate } = useAddNota();
  const [desconsiderado, setDesconsiderado] = useState<boolean>(!!item.desconsiderado);
  const { data: colaborador, isLoading } = useGetColaboradorById(empresaAtivaId || "", item.respondente);
  const podeDesconsiderarAvaliacao = useGetAuthorizations(ID_EXTRAS_DESCONSIDERAR_AVALIACAO);
  const data = item.updatedAt || item.createdAt;
  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const novoValor = event.currentTarget.checked;
      setDesconsiderado(novoValor);
      mutate(
        { ...item, desconsiderado: novoValor },
        {
          onSuccess: () => {
            void refetch();
            showNotification({
              message: novoValor ? "Avaliação desconsiderada" : "Avaliação reconsiderada",
              color: novoValor ? "orange" : "green",
              autoClose: 2500
            });
          },
          onError: () => {
            setDesconsiderado(!novoValor);
            showNotification({
              message: "Erro ao salvar. Tente novamente.",
              color: "red",
              autoClose: 2500
            });
          }
        }
      );
    },
    [item, mutate, refetch]
  );
  return (
    <Flex gap={"xl"} direction="row" wrap="wrap" align={"center"}>
      <Text size="sm">
        <strong>Avaliador:</strong> {isLoading ? "Carregando..." : colaborador?.nome} <strong>Fase:</strong>{" "}
        {faseTitulo} <strong>Nota:</strong> {item.nota} <strong>Justificativa</strong>: {item.justificativa}{" "}
        <strong>{item.updatedAt ? "Atualizada Em" : "Criada Em"}</strong>:{" "}
        {data instanceof Timestamp ? data.toDate().toLocaleString() : data?.toString()}
      </Text>
      {podeDesconsiderarAvaliacao.read && (
        <Checkbox label="Desconsiderar avaliação" checked={desconsiderado} onChange={handleCheckboxChange} readOnly />
      )}
    </Flex>
  );
}

type OutrasAvaliacoesProperties = {
  medias: TMedia[] | undefined;
  idEmpresa: string | undefined;
  currentUserId: string | undefined;
  opened: boolean;
  toggle: () => void;
  fases: Fase[];
  empresaAtivaId: string | undefined;
  refetch: () => void;
};

function OutrasAvaliacoes({
  medias,
  idEmpresa,
  currentUserId,
  opened,
  toggle,
  fases,
  empresaAtivaId,
  refetch
}: OutrasAvaliacoesProperties): React.ReactNode {
  const auth = useGetAuthorizations(ID_EXTRAS_VISUALIZAR_NOTAS_DE_AVALIADORES);
  if (!auth?.read) return null;
  const mediasFiltradas = medias?.filter(
    (media) => media.idEmpresaNota === idEmpresa && media.respondente !== currentUserId
  );
  return (
    <div style={{ width: "100%" }}>
      <Card shadow="none">
        <CardHeader opened={opened} toggle={toggle} qtdeAvaliacoes={mediasFiltradas?.length} />
        {opened && (
          <Stack mt="sm">
            {mediasFiltradas?.map((media) => {
              const faseItem = fases.find((row: Fase) => row?.id === media?.fase);
              if (!faseItem) return null;
              return (
                <AvaliacaoItem
                  key={media.id}
                  item={media}
                  faseTitulo={faseItem.titulo}
                  empresaAtivaId={empresaAtivaId}
                  refetch={refetch}
                />
              );
            })}
          </Stack>
        )}
      </Card>
    </div>
  );
}

const EstrategiaItem = ({ pergunta, numero, statusItem, respostas, grupo }: Properties) => {
  const [tipResposta, setTipResposta] = useState<string[]>([]);
  const empresaAtivaId = useUserStore((state) => state.activeEmpresaId);
  const [opened, { toggle }] = useDisclosure(false);
  const { data } = useGetProjetoFromUrl();
  const fases = (data?.fases || []) as Fase[];
  const faseItem = fases.find((item: Fase) => item?.id === statusItem?.status);
  const formatResposta = respostas.map((row) => {
    return row.perguntas.find((p: TRespostaPergunta) => p?.id === pergunta?.id);
  });
  const media = calcularMedia(formatResposta as Objeto[]) as number;
  const { data: questionario } = useGetQuestionarioFromUrl();
  const faseQuestionario = questionario?.fases || [];
  const { data: medias, refetch } = useQueryMedias({ id: pergunta?.id || "" });
  const currentUserId = getCurrentUser()?.uid;
  const { idEmpresa } = useParams<{ idEmpresa: string }>();

  useEffect(() => {
    if (!empresaAtivaId || !pergunta?.tipoDeResposta) {
      setTipResposta([]);
      return;
    }
    /**
     *
     */
    async function loadTipoResposta() {
      try {
        if (pergunta) {
          const tr = await getTiposDeRespostasItem(empresaAtivaId || "", pergunta.tipoDeResposta);
          const data = tr.data() as FirestoreTipoResposta | undefined;

          if (!data?.campos) {
            setTipResposta([]);
            return;
          }

          const lines = data.campos.map(({ campo, valor }) => `Campo: ${campo} = ${valor}`);
          setTipResposta(lines);
        }
      } catch (error) {
        console.error("falha ao carregar tipoResposta:", error);
        setTipResposta([]);
      }
    }

    void loadTipoResposta();
  }, [empresaAtivaId, pergunta?.tipoDeResposta]);

  if (typeof media === "string") {
    return <></>;
  }

  return (
    <>
      <Space h="md" />
      <Card withBorder shadow="none" style={{ flex: 1 }}>
        <Flex direction="column">
          <Flex gap="xs" direction="row" wrap="wrap" align={"center"}>
            <Avatar bg="#6188FA" color={"white"} radius="xl" size="sm">
              {numero}
            </Avatar>
            <Group align="center" justify="space-between">
              <Flex direction="column">
                {/* <Flex direction="row" justify="space-between" align="center" w="100%"> */}
                <Text fw={500} fz="md">
                  {pergunta?.nome}
                </Text>
                {/* </Flex> */}
                <Text size="xs">{pergunta?.descricao}</Text>
              </Flex>
              <Tooltip
                label={
                  <div style={{ whiteSpace: "pre-wrap" }}>
                    {tipResposta.map((line, index) => (
                      <span key={index}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </div>
                }
                withArrow
                position="right"
              >
                <IconInfoCircle size={32} />
              </Tooltip>
            </Group>
          </Flex>
          <Space h={"md"} />
          <Flex gap="xl" align="center" direction="row" wrap="wrap">
            <Group>
              {faseQuestionario && faseQuestionario.length > 0 ? (
                faseQuestionario.map((fase: Fase, index) => (
                  <Text key={fase.id || fase.titulo || String(index)} fw={"bold"} fz="sm">
                    {fase && fase?.titulo} - {Number(media && media?.toFixed(1))}
                  </Text>
                ))
              ) : (
                <></>
              )}
            </Group>
          </Flex>
          <Space h={"md"} />
          <Flex gap="xl" align="center" direction="row" wrap="wrap">
            <Stack>
              {medias
                ?.filter((media) => media.idEmpresaNota === idEmpresa && media.respondente === currentUserId)
                .map((media) => {
                  const faseItemMedia = fases.find((row: Fase) => row?.id === media?.fase);
                  if (!faseItemMedia) return null;
                  return <EditaNotaItem key={media.id} item={media} fase={faseItemMedia} />;
                })}
              {medias?.some(
                (media) =>
                  media.respondente === currentUserId &&
                  faseItem?.id === media?.fase &&
                  media.idEmpresaNota === idEmpresa
              ) ? (
                <></>
              ) : faseItem?.checked ? (
                <NotaItem pergunta={pergunta} fase={faseItem} grupo={grupo} />
              ) : (
                <></>
              )}
            </Stack>
            <OutrasAvaliacoes
              medias={medias}
              idEmpresa={idEmpresa}
              currentUserId={currentUserId}
              opened={opened}
              toggle={toggle}
              fases={fases}
              empresaAtivaId={empresaAtivaId}
              refetch={refetch}
            />
          </Flex>
        </Flex>
      </Card>
    </>
  );
};

export default EstrategiaItem;

/* eslint-disable sonarjs/cognitive-complexity */
/**
 * Calcula a média dos valores numéricos em um array de objetos.
 * @param array - Array de objetos contendo valores a serem incluídos no cálculo da média.
 * @returns A média dos valores numéricos no array, ou uma string contendo valores não numéricos se nenhum valor numérico for encontrado.
 */
function calcularMedia(array: Objeto[]) {
  let total = 0;
  let quantidade = 0;
  let respostasString = "";
  for (const objeto of array) {
    let valor;
    if (Array.isArray(objeto?.resposta)) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      for (const item of objeto?.resposta) {
        if (typeof item.valor === "number") {
          total += Number.parseFloat(item?.valor.toString());
          quantidade++;
        } else if (typeof item?.valor === "string") {
          respostasString += item.valor + ", ";
        }
      }
    } else if (typeof objeto?.resposta === "object" && objeto?.resposta !== null && objeto?.resposta !== undefined) {
      if (typeof objeto.resposta.valor === "number") {
        valor = Number.parseFloat(objeto.resposta.valor.toString());
        if (!Number.isNaN(valor)) {
          total += valor;
          quantidade++;
        }
      } else if (typeof objeto?.resposta?.valor === "string") {
        respostasString += objeto?.resposta?.valor + ", ";
      }
    } else if (typeof objeto?.resposta === "number") {
      valor = Number.parseFloat(objeto.resposta.toString());
      if (!Number.isNaN(valor)) {
        total += valor;
        quantidade++;
      }
    } else if (typeof objeto?.resposta === "string") {
      respostasString += objeto?.resposta + ", ";
    }
  }

  return quantidade === 0 ? respostasString.slice(0, STRING_TRIM_LENGTH) : total / quantidade;
}

/**
 * CardHeader
 * @param root0 - properties
 * @param root0.opened - opened
 * @param root0.toggle - toggle
 * @param root0.qtdeAvaliacoes - qtdeAvaliacoes
 * @returns CardHeader
 */
function CardHeader({ opened, toggle, qtdeAvaliacoes }: CardHeaderProperties) {
  if (!qtdeAvaliacoes || qtdeAvaliacoes === 0) return null;
  return (
    <Group align="center">
      <Button
        type="button"
        variant="transparent"
        size="compact-sm"
        rightSection={opened ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />}
        onClick={toggle}
      >
        Visualizar outras avaliações ({qtdeAvaliacoes})
      </Button>
    </Group>
  );
}
